import * as React from "react";

import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { LuLoaderCircle } from "react-icons/lu";

import { cn } from "@shared/utils/cn";

const buttonVariants = cva(
  "relative inline-flex items-center justify-center gap-2 rounded-full text-sm whitespace-nowrap font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    defaultVariants: {
      color: "primary",
      size: "default",
      variant: "default",
    },
    variants: {
      variant: {
        default: "",
        pastel: "",
        outline: "border-[1px]",

        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-black/10 hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:text-underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        icon: "h-9 w-9 text-lg",
        sm: "h-8 px-3 text-xs",
        lg: "h-10 px-8 text-md",
        xl: "h-14 py-2.5 px-10 text-lg",
      },
      color: {
        neutral: "bg-neutral text-white hover:bg-neutral/90",
        primary: "bg-primary text-white hover:bg-primary/90",
        white: "bg-white text-primary hover:bg-white/90",
        red: "bg-red text-white hover:bg-red/90",
      },
    },
    compoundVariants: [
      /* Ghost */
      {
        variant: "ghost",
        class: "bg-transparent",
      },
      {
        class: "text-black hover:bg-primary/20",
        color: "primary",
        variant: "ghost",
      },
      {
        class: "text-black hover:bg-neutral-50",
        color: "neutral",
        variant: "ghost",
      },
      {
        variant: "ghost",
        color: "white",
        class: "bg-transparent text-white border-white hover:bg-neutral-50",
      },

      /* Outline */
      {
        variant: "outline",
        class: "bg-transparent",
      },
      {
        variant: "outline",
        color: "white",
        class: "text-white border-white hover:bg-neutral-50 hover:text-primary",
      },
      {
        class: "bg-transparent text-primary border-primary hover:bg-primary/10",
        color: "primary",
        variant: "outline",
      },
      {
        class: "bg-transparent text-neutral border-neutral hover:bg-neutral-50",
        color: "neutral",
        variant: "outline",
      },
      {
        variant: "outline",
        color: "red",
        class:
          "bg-transparent text-red border-red hover:bg-red hover:text-white",
      },

      /* Pastel */

      {
        variant: "pastel",
        color: "primary",
        class: "bg-primary/10 text-primary hover:bg-primary/20",
      },
      {
        variant: "pastel",
        color: "neutral",
        class: "bg-neutral-50 text-neutral hover:bg-neutral-100",
      },

      /* Link */
      {
        variant: "link",
        class: "p-0 bg-transparent hover:bg-transparent hover:underline",
      },
      {
        variant: "link",
        color: "primary",
        class: "text-primary",
      },
      {
        variant: "link",
        color: "neutral",
        class: "text-neutral",
      },
    ],
  }
);

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      color,
      asChild = false,
      isLoading = false,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    const variantClassName = cn(
      buttonVariants({ className, color, size, variant })
    );

    return (
      <Comp
        className={cn(variantClassName, isLoading && "text-transparent")}
        ref={ref}
        {...props}
      >
        <div
          className={cn(
            "absolute w-full h-full left-0 top-0 grid place-content-center opacity-0",
            isLoading && "opacity-100",
            isLoading && variantClassName.match(/(text-(\w*))/g)
          )}
        >
          <LuLoaderCircle className="h-4 w-4 animate-spin" />
        </div>
        {children}
      </Comp>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
